import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import WaterDropRoundedIcon from "@mui/icons-material/WaterDropRounded";
import { createDripSession } from "../api/dripSessionsApi";
import { prettyError } from "../utils/prettyError";

const initialState = {
  admission: "",
  channel: "",
  fluid_label: "",
  target_mL_per_hr: "",
  drop_factor_gtt_per_mL: "",
  status: "ACTIVE",
};

export default function DripSessionCreateForm({ onCreated }) {
  const [form, setForm] = React.useState(initialState);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      setSubmitting(true);

      const payload = {
        admission: form.admission || null,
        channel: form.channel || null,
        fluid_label: form.fluid_label || null,
        target_mL_per_hr: form.target_mL_per_hr || null,
        drop_factor_gtt_per_mL: form.drop_factor_gtt_per_mL || null,
        status: form.status,
      };

      const created = await createDripSession(payload);
      setSuccess("Drip session created successfully.");
      setForm(initialState);

      if (onCreated) onCreated(created);
    } catch (err) {
      setError(prettyError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card
      sx={{
        borderRadius: 3,
        border: "1px solid #e6eef2",
        boxShadow: "none",
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" spacing={1.2} alignItems="center" mb={1}>
          <WaterDropRoundedIcon color="primary" />
          <Typography variant="h6" fontWeight={800}>
            Start New Drip Session
          </Typography>
        </Stack>

        <Typography variant="body2" color="text.secondary" mb={2}>
          Enter admission, channel and drip details
        </Typography>

        <Divider sx={{ mb: 2.5 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Admission ID"
                name="admission"
                value={form.admission}
                onChange={handleChange}
                required
                placeholder="Enter admission UUID / ID"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Channel ID"
                name="channel"
                value={form.channel}
                onChange={handleChange}
                placeholder="Enter sensor channel UUID / ID"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Fluid Label"
                name="fluid_label"
                value={form.fluid_label}
                onChange={handleChange}
                placeholder="Example: NS, RL, Dextrose"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Target mL/hr"
                name="target_mL_per_hr"
                type="number"
                value={form.target_mL_per_hr}
                onChange={handleChange}
                placeholder="80"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Drop Factor gtt/mL"
                name="drop_factor_gtt_per_mL"
                type="number"
                value={form.drop_factor_gtt_per_mL}
                onChange={handleChange}
                placeholder="20"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                select
                fullWidth
                label="Status"
                name="status"
                value={form.status}
                onChange={handleChange}
              >
                <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                <MenuItem value="PAUSED">PAUSED</MenuItem>
                <MenuItem value="STOPPED">STOPPED</MenuItem>
                <MenuItem value="COMPLETED">COMPLETED</MenuItem>
              </TextField>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<AddCircleOutlineRoundedIcon />}
                disabled={submitting}
                sx={{
                  mt: 1,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 700,
                  px: 3,
                }}
              >
                {submitting ? "Creating..." : "Create Session"}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
}